/**
 * The English text layer — translation, footnotes and short tafsir.
 *
 * ── Why this is bundled per mushaf page (L20) ───────────────────────────────────
 *
 * Exactly as the Arabic tafsir is. The assets arrive page-keyed already — 604 files plus
 * a manifest per edition — and that is not an accident of how they were built: per-surah
 * would mean a multi-megabyte fetch on the first click anywhere inside al-Baqarah, for a
 * panel that only ever shows one ayah at a time.
 *
 * ── The manifest is not metadata, it is an obligation ───────────────────────────
 *
 * Both translations are usable only on conditions, and the conditions are about DISPLAY:
 *
 *   Itani  — CC BY-NC-ND. The credit string "Translation by Talal Itani, ClearQuran.com"
 *            must appear, and the text must be verbatim (NoDerivs).
 *   Rwwad  — a seven-condition grant from QuranEnc, two of which this code can satisfy:
 *            credit the publisher and source, and STATE THE VERSION (`1.0.19`).
 *
 * So `manifest.json` ships, loads, and reaches the UI. An edition rendered without its
 * attribution is a licence breach, not a missing nicety — which is why `EnglishManifest`
 * has no optional attribution field to forget.
 *
 * The tafsir's own licence is UNSTATED (see `claude-plans/corpus-relicensing.md`); it is
 * bundled local-only on the same posture as the Arabic tafsir database, and this app is
 * never published.
 */
import { CORPUS_BASE } from './constants'

/** The two shipped translations. Ships both: Itani reads more smoothly, Rwwad has footnotes. */
export const TRANSLATION_EDITIONS = ['itani', 'rwwad'] as const
export type TranslationEdition = (typeof TRANSLATION_EDITIONS)[number]

export const DEFAULT_TRANSLATION: TranslationEdition = 'rwwad'

/**
 * One ayah of English.
 *
 * `footnotes` is keyed by the marker number that appears IN `text` as `[16]`. Rwwad
 * carries 2,566 bodies over 2,215 ayat; Itani has none at all, so the field is absent
 * rather than empty — "this edition has no footnotes" and "this ayah has none" are
 * different facts and the panel branches on them differently.
 */
export interface EnglishAyah {
  text: string
  footnotes?: Record<string, string>
}

export interface EnglishPage {
  page: number
  edition: string
  ayat: Record<string, EnglishAyah>
}

/**
 * Everything the UI is obliged to say about an edition, plus the counts the integrity
 * test pins. Deliberately NOT a loose object with optional fields: `attribution` and
 * `licence` are load-bearing.
 */
export interface EnglishManifest {
  id: string
  name: string
  kind: 'translation' | 'tafsir' | 'transliteration'
  language: 'en'
  attribution: string
  source: string
  licence: string
  /** Rwwad's grant requires this to be displayed. `null` where upstream publishes none. */
  version?: string | null
  hasFootnotes?: boolean
  ayahCount: number
}

const pad = (page: number) => String(page).padStart(3, '0')

/**
 * The transliteration editions that ship as DATA.
 *
 * `generated` is not here: it is computed from the Uthmani text at runtime by
 * `translit.ts` and fetches nothing.
 */
export const TRANSLIT_EDITIONS = ['syllabic'] as const
export type TranslitEdition = (typeof TRANSLIT_EDITIONS)[number]

export const englishUrls = {
  translation: (edition: TranslationEdition, page: number) =>
    `${CORPUS_BASE}/english/translation/${edition}/${pad(page)}.json`,
  translationManifest: (edition: TranslationEdition) =>
    `${CORPUS_BASE}/english/translation/${edition}/manifest.json`,
  tafsir: (page: number) => `${CORPUS_BASE}/english/tafsir/${pad(page)}.json`,
  tafsirManifest: () => `${CORPUS_BASE}/english/tafsir/manifest.json`,
  /**
   * NOT under `english/`. That directory is copied wholesale from the English asset
   * source by `corpus:sync`; this one is built by `scripts/fetch-translit.mjs` in THIS
   * repo, and nesting it inside a directory another script owns is how a `--force`
   * resync quietly deletes it.
   */
  translit: (edition: TranslitEdition, page: number) =>
    `${CORPUS_BASE}/translit/${edition}/${pad(page)}.json`,
  translitManifest: (edition: TranslitEdition) =>
    `${CORPUS_BASE}/translit/${edition}/manifest.json`,
} as const

async function getJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(url, { signal })
  if (!response.ok) throw new Error(`${url} → ${response.status}`)
  return (await response.json()) as T
}

export const loadTranslationPage = (
  edition: TranslationEdition,
  page: number,
  signal?: AbortSignal,
) => getJson<EnglishPage>(englishUrls.translation(edition, page), signal)

export const loadTranslationManifest = (edition: TranslationEdition, signal?: AbortSignal) =>
  getJson<EnglishManifest>(englishUrls.translationManifest(edition), signal)

export const loadEnglishTafsirPage = (page: number, signal?: AbortSignal) =>
  getJson<EnglishPage>(englishUrls.tafsir(page), signal)

export const loadEnglishTafsirManifest = (signal?: AbortSignal) =>
  getJson<EnglishManifest>(englishUrls.tafsirManifest(), signal)

export const loadTranslitPage = (edition: TranslitEdition, page: number, signal?: AbortSignal) =>
  getJson<EnglishPage>(englishUrls.translit(edition, page), signal)

export const loadTranslitManifest = (edition: TranslitEdition, signal?: AbortSignal) =>
  getJson<EnglishManifest>(englishUrls.translitManifest(edition), signal)

// ---------------------------------------------------------------------------
// Footnotes
// ---------------------------------------------------------------------------

/** A span of translation text: either prose, or a footnote marker pointing at a body. */
export type TextSpan = { kind: 'text'; text: string } | { kind: 'note'; marker: string }

/**
 * Split `text` into prose and footnote markers.
 *
 * Rwwad writes its markers inline as `[16]`, so rendering the field raw shows the reader
 * a bracketed number with nothing behind it. This turns them into spans the panel can
 * render as references.
 *
 * A MARKER WITH NO BODY STAYS PROSE. `[1]` also occurs as ordinary bracketed text in a
 * handful of places, and a reference that opens nothing is worse than a visible bracket —
 * so a marker is only a marker if `footnotes` actually has it.
 */
export function splitFootnotes(ayah: EnglishAyah): TextSpan[] {
  const bodies = ayah.footnotes
  if (!bodies) return [{ kind: 'text', text: ayah.text }]

  const spans: TextSpan[] = []
  let at = 0
  // Digits in brackets. ASCII only by construction — these are the build script's own
  // markers, not anything transcribed (L24's habit, applied to a Latin class).
  const pattern = /\[(\d{1,3})\]/g

  for (let m = pattern.exec(ayah.text); m !== null; m = pattern.exec(ayah.text)) {
    const marker = m[1]!
    if (!(marker in bodies)) continue
    if (m.index > at) spans.push({ kind: 'text', text: ayah.text.slice(at, m.index) })
    spans.push({ kind: 'note', marker })
    at = m.index + m[0].length
  }

  if (at < ayah.text.length) spans.push({ kind: 'text', text: ayah.text.slice(at) })
  return spans
}

/**
 * The translation with every footnote marker removed — for copy, share and the search
 * index. `[16]` in the middle of a sentence is noise in all three, and in the index it
 * would also make "16" a match for every footnoted ayah.
 */
export function plainTranslation(ayah: EnglishAyah): string {
  return splitFootnotes(ayah)
    .filter((s): s is { kind: 'text'; text: string } => s.kind === 'text')
    .map((s) => s.text)
    .join('')
    .replace(/\s+/g, ' ')
    .trim()
}
